import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { formatDateLong, capitalize } from "@/lib/format";
import KpiCard from "@/components/dashboard/KpiCard";
import ActivityTimeline from "@/components/dashboard/ActivityTimeline";
import RevenueChart from "@/components/dashboard/RevenueChart";
import DistributionChart from "@/components/dashboard/DistributionChart";
import AlertsPanel from "@/components/dashboard/AlertsPanel";
import GettingStartedChecklist from "@/components/dashboard/GettingStartedChecklist";
import { Ticket, Receipt, UserPlus, Loader2, MapPin } from "lucide-react";

const PERIODS = [
  { k: "aujourdhui", label: "Aujourd'hui" },
  { k: "hier", label: "Hier" },
  { k: "7j", label: "7 jours" },
  { k: "ce_mois", label: "Ce mois" },
  { k: "mois_dernier", label: "Mois dernier" },
];

export default function Dashboard() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user, orgId, can } = useAuth();
  const [period, setPeriod] = useState("aujourdhui");
  const [range, setRange] = useState("7d");

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", orgId, period, range],
    queryFn: async () => {
      const { data } = await api.get("/api/dashboard", {
        params: { organization_id: orgId, period, range },
      });
      return data;
    },
    enabled: !!orgId,
  });

  const currency = data?.organization?.currency || "EUR";

  return (
    <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="dashboard-greeting">
            {t("dash.greeting")} {user?.name?.split(" ")[0]} 👋
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
            <span>{capitalize(formatDateLong())}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              <MapPin className="h-3 w-3" /> {data?.active_location ? `${data.active_location.name}${data.active_location.city ? " · " + data.active_location.city : ""}` : "Tous les établissements"}
            </span>
          </div>
        </div>
        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/reservations?new=1")} data-testid="quick-action-new-reservation-button"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700">
            <Ticket className="h-4 w-4" /> {t("dash.quick.reservation")}
          </button>
          <button onClick={() => navigate("/depenses?new=1")} data-testid="quick-action-new-expense-button"
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50">
            <Receipt className="h-4 w-4" /> {t("dash.quick.expense")}
          </button>
          <button onClick={() => navigate("/reservations?new=1")} data-testid="quick-action-new-client-button"
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50">
            <UserPlus className="h-4 w-4" /> {t("dash.quick.client")}
          </button>
        </div>
      </div>

      <GettingStartedChecklist />

      {/* Period filters */}
      <div className="mt-6 flex flex-wrap gap-1.5" data-testid="period-filters">
        {PERIODS.map((p) => (
          <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`filter-date-${p.k}-button`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${period === p.k ? "bg-slate-900 text-white shadow-sm" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {isLoading || !data ? (
        <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {data.kpis.map((kpi, i) => <KpiCard key={kpi.id} kpi={kpi} currency={currency} index={i} />)}
          </div>

          {/* Timeline + Alerts */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2"><ActivityTimeline items={data.timeline} onOpen={(id) => navigate(`/reservations?open=${id}`)} /></div>
            <div><AlertsPanel alerts={data.alerts} onAction={(a) => a.link && navigate(a.link)} /></div>
          </div>

          {/* Charts (financial — finance.view only) */}
          {can("finance.view") && (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <RevenueChart data={data.revenue_series} currency={currency} range={range} onRange={setRange} />
            </div>
            <div><DistributionChart data={data.distribution} currency={currency} subtitle={data.distribution_label} /></div>
          </div>
          )}
        </>
      )}
    </div>
  );
}
